import React from 'react';
import { Target, TrendingUp } from 'lucide-react';
import { formatStat } from '../../utils/formatters';

/**
 * Hedeflenen Ciro ve Trafik — Ay sonu tahmini tablosu.
 * Mevcut ayın kısmi verisine dayanarak ay sonu projeksiyonu yapar.
 * AOV artışı ve CR stabilitesi dikkate alınır.
 * @param {object} props
 * @param {number} [props.targetCost] - Sabit maliyet varsayımı (örn. 500K bütçe)
 * @param {number} [props.targetCr] - Hedef CR % (örn. 0.16)
 * @param {number} [props.sessionsBoost] - Sessions çarpanı (örn. 1.15 = %15 artış)
 * @param {number} [props.maxSessions] - Sessions üst sınırı
 * @param {number} [props.maxRevenue] - Revenue üst sınırı
 */
export function HedeflenenCirTrafikTable({ gaMonthlySummary, allCostsByMonth, targetCost, targetCr, sessionsBoost, maxSessions, maxRevenue }) {
  if (!gaMonthlySummary?.monthlyData?.length) {
    return <p className="text-xs text-gray-400 text-center py-4">Veri yok</p>;
  }

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const currentMonthKey = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
  const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
  const daysElapsed = Math.min(now.getDate(), daysInMonth);

  const prevMonthKey = currentMonth === 1
    ? `${currentYear - 1}-12`
    : `${currentYear}-${String(currentMonth - 1).padStart(2, '0')}`;
  const currentMonthData = gaMonthlySummary.monthlyData.find(m => m.displayMonthKey === currentMonthKey);
  const prevMonthData = gaMonthlySummary.monthlyData.find(m => m.displayMonthKey === prevMonthKey);

  if (!currentMonthData || daysElapsed < 1) {
    return (
      <div className="text-sm text-gray-500 py-4 text-center">
        Bu ay için henüz yeterli veri yok veya ay tamamlandı.
      </div>
    );
  }

  const sessions = currentMonthData.sessions || 0;
  const revenue = currentMonthData.totalRevenue || 0;
  const transactions = currentMonthData.eventCount || 0;
  const cr = currentMonthData.conversionRate || 0;
  const aov = transactions > 0 ? revenue / transactions : 0;

  const projectionFactor = daysInMonth / daysElapsed;
  const baseProjectedSessions = sessions * projectionFactor;
  let projectedSessions = Math.round(baseProjectedSessions * (sessionsBoost ?? 1));
  if (maxSessions != null && projectedSessions > maxSessions) projectedSessions = maxSessions;
  const projectedCr = targetCr ?? cr;
  const projectedTransactions = Math.round(projectedSessions * (projectedCr / 100));
  const projectedAov = aov;
  let projectedRevenue = (projectedTransactions > 0 && projectedAov > 0)
    ? Math.round(projectedTransactions * projectedAov)
    : Math.round(revenue * projectionFactor);
  if (maxRevenue != null && projectedRevenue > maxRevenue) projectedRevenue = maxRevenue;

  const costSoFar = Number((allCostsByMonth || {})[currentMonthData.month] || 0);
  const projectedCost = targetCost ?? Math.round(costSoFar * projectionFactor);
  const projectedRoi = projectedCost > 0 ? (projectedRevenue / projectedCost) : 0;

  const revenueTarget = 1500000;
  const revenueTargetLabel = '₺1.5M';
  const reachesTarget = projectedRevenue >= revenueTarget;
  const dailyRevenue = revenue / daysElapsed;
  const daysToTarget = dailyRevenue > 0 ? Math.ceil(revenueTarget / dailyRevenue) : null;

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50/80 divide-x divide-gray-100">
              <th className="py-3 px-4 text-left font-semibold text-gray-600">Dönem</th>
              <th className="py-3 px-4 text-right font-semibold text-gray-600">Sessions</th>
              <th className="py-3 px-4 text-right font-semibold text-gray-600">Transactions</th>
              <th className="py-3 px-4 text-right font-semibold text-gray-600">Revenue (₺)</th>
              <th className="py-3 px-4 text-right font-semibold text-gray-600">All Costs (₺)</th>
              <th className="py-3 px-4 text-right font-semibold text-gray-600">ROI</th>
              <th className="py-3 px-4 text-right font-semibold text-gray-600">AOV (₺)</th>
              <th className="py-3 px-4 text-right font-semibold text-gray-600">CR %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {prevMonthData && (
              <tr className="divide-x divide-gray-100">
                <td className="py-3 px-4 font-medium text-gray-700">{prevMonthData.month}</td>
                <td className="py-3 px-4 text-right">{formatStat(prevMonthData.sessions)}</td>
                <td className="py-3 px-4 text-right">{prevMonthData.eventCount?.toLocaleString()}</td>
                <td className="py-3 px-4 text-right">₺{prevMonthData.totalRevenue?.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</td>
                <td className="py-3 px-4 text-right">₺{Number((allCostsByMonth || {})[prevMonthData.month] || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</td>
                <td className="py-3 px-4 text-right">
                  {(() => {
                    const c = Number((allCostsByMonth || {})[prevMonthData.month] || 0);
                    return c > 0 ? `${(prevMonthData.totalRevenue / c).toFixed(2)}x` : '—';
                  })()}
                </td>
                <td className="py-3 px-4 text-right">₺{prevMonthData.eventCount > 0 ? (prevMonthData.totalRevenue / prevMonthData.eventCount).toLocaleString('tr-TR', { maximumFractionDigits: 0 }) : '—'}</td>
                <td className="py-3 px-4 text-right">{prevMonthData.conversionRate?.toFixed(2)}%</td>
              </tr>
            )}
            <tr className="divide-x divide-gray-100 bg-blue-50/30">
              <td className="py-3 px-4 font-medium text-gray-800">{currentMonthData.month} (bugüne kadar)</td>
              <td className="py-3 px-4 text-right">{formatStat(sessions)}</td>
              <td className="py-3 px-4 text-right">{transactions.toLocaleString()}</td>
              <td className="py-3 px-4 text-right">₺{revenue.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</td>
              <td className="py-3 px-4 text-right">₺{costSoFar.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</td>
              <td className="py-3 px-4 text-right">{costSoFar > 0 ? `${(revenue / costSoFar).toFixed(2)}x` : '—'}</td>
              <td className="py-3 px-4 text-right">₺{aov.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</td>
              <td className="py-3 px-4 text-right">{cr.toFixed(2)}%</td>
            </tr>
            <tr className="divide-x divide-gray-100 bg-emerald-50/50 font-semibold">
              <td className="py-3 px-4 font-bold text-emerald-800 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                {currentMonthData.month} ay sonu tahmini
              </td>
              <td className="py-3 px-4 text-right text-emerald-800">{formatStat(projectedSessions)}</td>
              <td className="py-3 px-4 text-right text-emerald-800">{projectedTransactions.toLocaleString()}</td>
              <td className="py-3 px-4 text-right text-emerald-800 font-bold">₺{projectedRevenue.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</td>
              <td className="py-3 px-4 text-right text-emerald-800">₺{projectedCost.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</td>
              <td className="py-3 px-4 text-right text-emerald-800">{projectedRoi.toFixed(2)}x</td>
              <td className="py-3 px-4 text-right text-emerald-800">₺{projectedAov.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</td>
              <td className="py-3 px-4 text-right text-emerald-800">{projectedCr.toFixed(2)}%</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-blue-600" />
          <span className="text-sm font-medium text-gray-700">{revenueTargetLabel} ciro hedefi</span>
        </div>
        <span className="text-xs text-gray-400">
          • {targetCost != null ? `Varsayım: ₺${Math.round(targetCost/1000)}K maliyet · CR %${projectedCr} · sessions ${sessionsBoost && sessionsBoost > 1 ? `%${Math.round((sessionsBoost - 1) * 100)} artış` : 'mevcut tempo'}` : `Projeksiyon: ${daysElapsed}/${daysInMonth} gün verisi × (${daysInMonth}/${daysElapsed})`}
        </span>
      </div>
    </div>
  );
}
